import React from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Phone, CheckCircle, Clock, Shield, Lock, Eye, ArrowRight } from 'lucide-react';
import Breadcrumbs from '../components/Breadcrumbs';

const TresoroeffnungPage = () => {
  const phone = '+4923139085116';
  const displayPhone = '0231 39085116';

  const faqData = [
    {
      question: 'Können Inhalte im Tresor beschädigt werden?',
      answer: 'Wir arbeiten äußerst sorgfältig und wählen die schonendste Öffnungsmethode. In den allermeisten Fällen bleiben die Inhalte unbeschädigt. Bei der Auswahl der Methode berücksichtigen wir die Sensibilität der Inhalte.'
    },
    {
      question: 'Welche Infos benötigen Sie vorab?',
      answer: 'Hilfreich sind: Tresortyp/Marke, ungefähre Größe, Art des Schlosses (Zahlenschloss, Schlüsselschloss, elektronisch), Grund der Öffnung (Code vergessen, Schlüssel verloren, defekt).'
    },
    {
      question: 'Öffnen Sie auch alte oder unbekannte Marken?',
      answer: 'Ja, wir haben Erfahrung mit vielen verschiedenen Tresortypen und -marken. Auch bei älteren oder weniger bekannten Modellen finden wir meist eine Lösung.'
    },
    {
      question: 'Wie lange dauert eine Tresoröffnung?',
      answer: 'Das hängt vom Tresortyp und der Sicherheitsstufe ab. Manche Tresore lassen sich in wenigen Minuten öffnen, bei hochsicheren Modellen kann es länger dauern. Wir informieren Sie vorab über den voraussichtlichen Zeitaufwand.'
    },
    {
      question: 'Was kostet eine Tresoröffnung?',
      answer: 'Die Kosten variieren je nach Tresortyp, Sicherheitsstufe und Aufwand. Wir besprechen dies transparent mit Ihnen, bevor wir beginnen. Eine Anfahrt für eine Begutachtung kann oft sinnvoll sein.'
    }
  ];

  return (
    <>
      <Helmet>
        <title>Tresoröffnung in Dortmund – Hilfe bei defektem Tresor & verlorenem Code</title>
        <meta name="description" content="Ihr Tresor lässt sich nicht mehr öffnen? Schlüsseldienst Dortmund hilft bei Tresoröffnung – diskret, fachgerecht & möglichst schadenarm. Beratung unter 0231 39085116." />
        <link rel="canonical" href="https://schluesseldienst.services/leistungen/tresoroeffnung/" />
      </Helmet>

      <div className="container mx-auto px-4 py-12">
        <Breadcrumbs items={[
          { label: 'Leistungen', path: '/leistungen' },
          { label: 'Tresoröffnung' }
        ]} />
        
        <h1 className="text-4xl sm:text-5xl font-bold mb-8 text-gray-900">
          Tresoröffnung in Dortmund
        </h1>

        <div className="max-w-4xl mb-12">
          <p className="text-lg text-gray-700 mb-4">
            <strong>Code vergessen? Schlüssel verloren? Tresorschloss defekt?</strong> Ein nicht zu öffnender 
            Tresor ist ein großes Problem – vor allem wenn wichtige Dokumente, Wertgegenstände oder Bargeld 
            darin liegen. Keine Sorge: Als spezialisierter Schlüsseldienst in Dortmund helfen wir Ihnen bei 
            der professionellen Tresoröffnung.
          </p>
          <p className="text-lg text-gray-700 mb-4">
            Wir arbeiten diskret, vertraulich und mit größter Sorgfalt. Unser Ziel ist es, Ihren Tresor 
            möglichst schadenarm zu öffnen und die Inhalte unbeschädigt zu bergen.
          </p>
          <p className="text-lg text-gray-700 mb-6">
            Die Schössow Dienstleistungen UG verfügt über die nötige Erfahrung und das Spezialwerkzeug 
            für verschiedene Tresortypen. Wir beraten Sie vorab ausführlich und transparent über die 
            Kosten und den Ablauf.
          </p>
          
          <div className="bg-orange-50 border-2 border-orange-200 rounded-xl p-6 mb-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">Tresor lässt sich nicht öffnen?</h3>
                <p className="text-gray-700">Diskrete und fachgerechte Hilfe – jetzt anrufen!</p>
              </div>
              <a
                href={`tel:${phone}`}
                className="inline-flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-full font-bold text-lg transition-all shadow-lg whitespace-nowrap"
              >
                <Phone size={20} />
                {displayPhone}
              </a>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Vorteile auf einen Blick</h2>
          <div className="grid md:grid-cols-2 gap-4">
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Diskrete, vertrauliche Arbeitsweise</strong> – Ihre Privatsphäre ist geschützt</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Fachgerechter Umgang</strong> mit verschiedenen Tresorarten</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Transparente Kostenabsprache</strong> vor Beginn der Arbeit</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Möglichst schonende Öffnung</strong> zum Schutz der Inhalte</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Erfahrung mit vielen Tresortypen</strong> – auch älteren Modellen</p>
            </div>
            <div className="flex gap-3">
              <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-1" />
              <p className="text-gray-700"><strong>Beratung zur weiteren Nutzung</strong> nach der Öffnung</p>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Wie läuft eine Tresoröffnung ab?</h2>
          <div className="space-y-6">
            <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-600">
              <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                <span className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">1</span>
                Analyse & Beratung
              </h3>
              <p className="text-gray-700">
                Zunächst analysieren wir den Tresortyp, das Schloss und die Situation. Wir besprechen mit Ihnen 
                die verschiedenen Öffnungsmethoden, den voraussichtlichen Zeitaufwand und die Kosten.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-600">
              <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                <span className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">2</span>
                Auswahl der Methode
              </h3>
              <p className="text-gray-700">
                Je nach Tresortyp gibt es verschiedene Öffnungsmethoden. Wir wählen immer die schonendste 
                Methode, die noch erfolgversprechend ist. Bei hochwertigen Tresoren dauert dies länger, 
                schützt aber die Inhalte besser.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-600">
              <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                <span className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">3</span>
                Möglichst schonende Öffnung
              </h3>
              <p className="text-gray-700">
                Mit Spezialwerkzeug und viel Erfahrung öffnen wir den Tresor. Bei Code-Tresoren versuchen 
                wir es oft zunächst mit mechanischen oder elektronischen Manipulationstechniken, bevor 
                wir zu invasiveren Methoden greifen.
              </p>
            </div>
            
            <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-orange-600">
              <h3 className="text-xl font-bold mb-2 flex items-center gap-2">
                <span className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold">4</span>
                Bergung & Beratung
              </h3>
              <p className="text-gray-700">
                Nach erfolgreicher Öffnung bergen wir gemeinsam mit Ihnen die Inhalte. Wir beraten Sie, 
                ob der Tresor repariert werden kann oder ein neues Schloss benötigt wird.
              </p>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Sicherheit & Diskretion</h2>
          <div className="bg-amber-50 border-l-4 border-amber-500 p-6 rounded-lg">
            <div className="flex gap-3">
              <Eye className="w-6 h-6 text-amber-600 flex-shrink-0" />
              <div>
                <h3 className="font-bold text-lg mb-2 text-gray-900">Absolute Vertraulichkeit</h3>
                <p className="text-gray-700 mb-3">
                  Wir behandeln Ihren Auftrag mit größter Diskretion. Alle Informationen über Ihren Tresor, 
                  die Inhalte und die Öffnung bleiben vertraulich. Wir geben keinerlei Informationen an Dritte weiter.
                </p>
                <p className="text-gray-700">
                  Selbstverständlich benötigen wir einen Nachweis, dass Sie der rechtmäßige Eigentümer des 
                  Tresors sind – dies dient auch Ihrem Schutz.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Preise & Aufwand</h2>
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl p-8 border-2 border-orange-200">
            <div className="bg-white rounded-lg p-6 shadow-md">
              <h3 className="text-xl font-bold mb-3 flex items-center gap-2">
                <Lock className="w-5 h-5 text-orange-600" />
                Individuelle Preisgestaltung
              </h3>
              <p className="text-gray-700 mb-4">
                Die Kosten für eine Tresoröffnung hängen stark vom Tresortyp, der Sicherheitsstufe und 
                dem Aufwand ab. Ein einfacher Bürotresor lässt sich schneller und günstiger öffnen als 
                ein hochsicherer Wertschutzschrank.
              </p>
              <p className="text-gray-700 mb-4">
                <strong>Deshalb gilt:</strong> Wir können Ihnen erst nach einer Begutachtung vor Ort oder 
                nach Beschreibung des Tresors am Telefon einen realistischen Preis nennen. Diese Transparenz 
                ist uns wichtig – Sie erfahren die Kosten, bevor wir mit der Arbeit beginnen.
              </p>
              <ul className="space-y-2">
                <li className="flex gap-2 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Keine versteckten Gebühren</span>
                </li>
                <li className="flex gap-2 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Transparente Absprache vor Beginn</span>
                </li>
                <li className="flex gap-2 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                  <span>Faire Preise für unsere Leistung</span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="max-w-4xl mb-12">
          <h2 className="text-3xl font-bold mb-6 text-gray-900">Häufige Fragen</h2>
          <div className="space-y-4">
            {faqData.map((faq, index) => (
              <div key={index} className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold mb-3 text-gray-900">{faq.question}</h3>
                <p className="text-gray-700">{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-gray-900 text-white rounded-2xl p-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Tresor öffnen lassen? Jetzt beraten lassen!</h2>
          <p className="text-lg mb-6 text-gray-300">
            Diskret • Fachgerecht • Möglichst schadenarm<br />
            Rufen Sie uns für eine unverbindliche Beratung an
          </p>
          <a
            href={`tel:${phone}`}
            className="inline-flex items-center gap-3 bg-orange-600 hover:bg-orange-700 text-white px-8 py-4 rounded-full font-bold text-xl transition-all shadow-lg hover:shadow-xl"
          >
            <Phone size={24} />
            {displayPhone}
          </a>
        </div>

        <div className="mt-12 max-w-4xl">
          <h3 className="text-xl font-bold mb-4 text-gray-900">Weitere Leistungen</h3>
          <div className="flex flex-wrap gap-4">
            <Link to="/leistungen" className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 px-6 py-3 rounded-full font-semibold transition-all">
              Alle Leistungen <ArrowRight size={18} />
            </Link>
            <Link to="/leistungen/schlosswechsel" className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 px-6 py-3 rounded-full font-semibold transition-all">
              Schlosswechsel <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default TresoroeffnungPage;
